import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@material-ui/core";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Alert, CircularProgress } from "@mui/material";
import { TextField, Button, FormControl, Select, MenuItem } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { sdmTableStyles, getHeaderCellStyle } from "../SDMComponents/sdmTableStyles";
import LabeledTextField from "../SDMComponents/LabeledTextField";
import LargeTextField from "../SDMComponents/LargeTextField";
import getAPI from "@API/index";

const formatAmount = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const str = String(val);
  const parts = str.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "\u00A0");
  return parts.join(".");
};

const FormattedAmountInput = ({ value, onChange, ...props }) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const handleFocus = () => {
    setIsFocused(true);
    setInputValue(String(value ?? ""));
  };

  const handleBlur = () => {
    setIsFocused(false);
    const cleaned = inputValue.replace(/[\u00A0\s]/g, "").replace(",", ".");
    onChange(cleaned);
  };

  const handleChange = (e) => {
    const filtered = e.target.value.replace(/[^\d.,-]/g, "").replace(",", ".");
    setInputValue(filtered);
  };

  return (
    <TextField
      type="text"
      value={isFocused ? inputValue : formatAmount(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{ style: { textAlign: "right" } }}
      {...props}
    />
  );
};

const BorrowerInfoTab = ({ formData, handleInputChange, handleTableCellChange, handleAddRow, handleDeleteRow, isMfo, clientId, onSave, onDataRefresh, activityTypes = [] }) => {
  const { t } = useTranslation();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const pollIntervalRef = useRef(null);

  const { industries, activityTypesMap } = useMemo(() => {
    const industriesSet = new Set();
    const typesMap = {};
    activityTypes.forEach(item => {
      if (item.industry) {
        industriesSet.add(item.industry);
        if (!typesMap[item.industry]) {
          typesMap[item.industry] = [];
        }
        if (item.activity_type) {
          typesMap[item.industry].push(item.activity_type);
        }
      }
    });
    return {
      industries: Array.from(industriesSet).sort(),
      activityTypesMap: typesMap,
    };
  }, [activityTypes]);

  const isAiFieldsDisabled = isAnalyzing || formData.borrowerInfo?.aiAnalysisStatus === "pending";

  const startPolling = () => {
    // Clear any existing interval first
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    const intervalId = setInterval(async () => {
      if (onDataRefresh) {
        await onDataRefresh();
      }
    }, 5000); // Poll every 5 seconds

    pollIntervalRef.current = intervalId;
  };

  const handleAIAnalysis = async () => {
    if (!formData.borrowerInfo.industry || !formData.borrowerInfo.typeOfActivity) {
      setAnalysisError("Отрасль и вид деятельности должны быть заполнены");
      return;
    }

    try {
      setIsAnalyzing(true);
      setAnalysisError(null);

      // Save data before starting AI analysis
      if (onSave) {
        await onSave();
      }

      await getAPI().request({
        url: `/api/CreditAnalysis/client/${clientId}/professional-judgment/ai-analysis`,
        method: "POST"
      });

      // Start polling for updates
      startPolling();

      // Refresh data immediately to show pending status
      if (onDataRefresh) {
        await onDataRefresh();
      }
    } catch (error) {
      console.error("Error starting AI analysis:", error);
      setAnalysisError(error.response?.data?.error || "Ошибка запуска AI анализа");
      setIsAnalyzing(false);
    }
  };

  // Monitor AI analysis status
  useEffect(() => {
    const status = formData.borrowerInfo?.aiAnalysisStatus;

    if (status === "pending") {
      if (!isAnalyzing) {
        setIsAnalyzing(true);
      }
      if (!pollIntervalRef.current) {
        startPolling();
      }
    } else {
      if (isAnalyzing) {
        setIsAnalyzing(false);
      }
      // Stop polling when analysis is completed or failed
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }

    if (status === "failed") {
      setAnalysisError(formData.borrowerInfo?.aiAnalysisError || "Ошибка при выполнении AI анализа");
    } else if (status !== "pending") {
      setAnalysisError(null);
    }
  }, [formData.borrowerInfo?.aiAnalysisStatus, formData.borrowerInfo?.aiAnalysisError]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {/* INN */}
      <LabeledTextField
        label={t("creditAnalysis.borrowerInformation.inn")}
        value={formData.borrowerInfo.inn}
        onChange={(value) => handleInputChange("inn", value.replace(/\D/g, ""))}
      />

      {/* Actual address */}
      <LabeledTextField
        label={t("creditAnalysis.borrowerInformation.actualAddress")}
        value={formData.borrowerInfo.actualAddress}
        onChange={(value) => handleInputChange("actualAddress", value)}
      />

      {/* Warehouse address */}
      <LabeledTextField
        label={t("creditAnalysis.borrowerInformation.warehouseAddress")}
        value={formData.borrowerInfo.warehouseAddress}
        onChange={(value) => handleInputChange("warehouseAddress", value)}
      />

      {/* Legal premises documents */}
      {isMfo && (
        <LargeTextField
          label={t("creditAnalysis.borrowerInformation.legalPremisesDocuments")}
          value={formData.borrowerInfo.legalPremisesDocuments}
          onChange={(value) => handleInputChange("legalPremisesDocuments", value)}
        />
      )}

      {/* Actual premises documents */}
      <LargeTextField
        label={t("creditAnalysis.borrowerInformation.actualPremisesDocuments")}
        value={formData.borrowerInfo.actualPremisesDocuments}
        onChange={(value) => handleInputChange("actualPremisesDocuments", value)}
      />

      {/* Warehouse premises documents */}
      <LargeTextField
        label={t("creditAnalysis.borrowerInformation.warehousePremisesDocuments")}
        value={formData.borrowerInfo.warehousePremisesDocuments}
        onChange={(value) => handleInputChange("warehousePremisesDocuments", value)}
      />

      {/* Verification date */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "16px" }}>
        <Box sx={{ width: "250px", flexShrink: 0, fontSize: "14px", fontWeight: 500, color: "#1A1E24" }}>
          {t("creditAnalysis.borrowerInformation.verificationDate")}
        </Box>
        <TextField
          type="date"
          value={formData.borrowerInfo.verificationDate}
          onChange={(e) => handleInputChange("verificationDate", e.target.value)}
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "14px",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#1976d2" },
              "&.Mui-focused fieldset": { borderColor: "#1976d2" },
            },
          }}
        />
      </Box>

      {/* Scope of activity */}
      {!isMfo && (
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "16px" }}>
        <Box sx={{ width: "250px", flexShrink: 0, fontSize: "14px", fontWeight: 500, color: "#1A1E24" }}>
          {t("creditAnalysis.borrowerInformation.scopeOfActivity")}
        </Box>
        <FormControl fullWidth size="small">
          <Select
            value={formData.borrowerInfo.scopeOfActivity}
            onChange={(e) => handleInputChange("scopeOfActivity", e.target.value)}
            displayEmpty
            sx={{
              fontSize: "14px",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#1976d2" },
              "&.Mui-focused fieldset": { borderColor: "#1976d2" },
            }}
          >
            <MenuItem value=""><em>Выберите сферу деятельности</em></MenuItem>
            <MenuItem value="Производственная сфера и прочие предприятия">Производственная сфера и прочие предприятия</MenuItem>
            <MenuItem value="Посреднические и торговые предприятия">Посреднические и торговые предприятия</MenuItem>
          </Select>
        </FormControl>
      </Box>
      )}

      {/* Industry */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "16px" }}>
        <Box sx={{ width: "250px", flexShrink: 0, fontSize: "14px", fontWeight: 500, color: "#1A1E24" }}>
          {t("creditAnalysis.borrowerInformation.industry")}
        </Box>
        <FormControl fullWidth size="small" disabled={isAiFieldsDisabled}>
          <Select
            value={formData.borrowerInfo.industry}
            onChange={(e) => {
              handleInputChange("industry", e.target.value);
              handleInputChange("typeOfActivity", "");
            }}
            displayEmpty
            sx={{
              fontSize: "14px",
              "& fieldset": { borderColor: "#e0e0e0" },
              "&:hover fieldset": { borderColor: "#1976d2" },
              "&.Mui-focused fieldset": { borderColor: "#1976d2" },
            }}
          >
            <MenuItem value=""><em>Выберите отрасль</em></MenuItem>
            {industries.map((industry) => (
              <MenuItem key={industry} value={industry}>{industry}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Activity type */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px", gap: "16px" }}>
        <Box sx={{ width: "250px", flexShrink: 0, fontSize: "14px", fontWeight: 500, color: "#1A1E24" }}>
          {t("creditAnalysis.borrowerInformation.typeOfActivity")}
        </Box>
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          disabled={!formData.borrowerInfo.industry || isAiFieldsDisabled}
          options={(formData.borrowerInfo.industry && activityTypesMap[formData.borrowerInfo.industry]) || []}
          value={formData.borrowerInfo.typeOfActivity || ""}
          inputValue={formData.borrowerInfo.typeOfActivity || ""}
          onChange={(e, newValue) => {
            handleInputChange("typeOfActivity", newValue || "");
          }}
          onInputChange={(e, newValue, reason) => {
            if (reason === "input") {
              handleInputChange("typeOfActivity", newValue);
            }
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Выберите или введите вид деятельности" />
          )}
        />
      </Box>

      {/* AI Analysis Button */}
      <Box sx={{ marginBottom: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Button
            variant="contained"
            startIcon={isAiFieldsDisabled ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleAIAnalysis}
            disabled={
              isAiFieldsDisabled ||
              !formData.borrowerInfo.industry ||
              !formData.borrowerInfo.typeOfActivity
            }
            sx={{
              backgroundColor: "#6B4CE6",
              "&:hover": { backgroundColor: "#5A3DD4" },
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {isAiFieldsDisabled ? "AI анализ выполняется..." : "Запросить AI анализ"}
          </Button>

          {(analysisError || formData.borrowerInfo?.aiAnalysisStatus === "completed") && (
            <Alert
              severity={analysisError ? "error" : "success"}
              sx={{
                margin: 0,
                flex: 1,
                display: "flex",
                alignItems: "center",
                padding: "0px 16px",
                minHeight: "36.5px"
              }}
            >
              {analysisError || "AI анализ успешно завершен"}
            </Alert>
          )}
        </Box>
      </Box>

      {/* AI-populated fields with loader overlay */}
      <Box sx={{ position: "relative" }}>
        {isAiFieldsDisabled && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              borderRadius: "8px"
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress size={40} />
              <Box sx={{ marginTop: "16px", fontSize: "14px", color: "#666" }}>
                AI анализирует данные заемщика...
              </Box>
            </Box>
          </Box>
        )}

        {/* Activity description */}
        <LargeTextField
          label={t("creditAnalysis.borrowerInformation.activityDescription")}
          value={formData.borrowerInfo?.activityDescription || ""}
          onChange={(value) => handleInputChange("activityDescription", value)}
          disabled={isAiFieldsDisabled}
          backgroundColor="#F0EBFF"
        />

        {/* Market information */}
        <LargeTextField
          label={t("creditAnalysis.borrowerInformation.marketInformation")}
          value={formData.borrowerInfo?.marketInformation || ""}
          onChange={(value) => handleInputChange("marketInformation", value)}
          disabled={isAiFieldsDisabled}
          backgroundColor="#F0EBFF"
        />
      </Box>

      {/* Employee count */}
      <LabeledTextField
        label={t("creditAnalysis.borrowerInformation.employeeCount")}
        value={formData.borrowerInfo.employeeCount}
        onChange={(value) => handleInputChange("employeeCount", value)}
      />

      {/* Information on existing obligations */}
      <Box sx={sdmTableStyles.tableWrapper}>
        <Box sx={sdmTableStyles.headerSection}>
          <Box sx={sdmTableStyles.sectionTitle}>
            Информация о действующих обязательствах Заемщика / Группы связанных заемщиков в Банке
          </Box>
        </Box>
        <TableContainer component={Paper} elevation={0} sx={sdmTableStyles.tableContainer}>
          <Table size={sdmTableStyles.tableSize}>
            <TableHead>
              <TableRow sx={sdmTableStyles.headerRow}>
                <TableCell sx={getHeaderCellStyle(150)}>Наименование заемщика</TableCell>
                <TableCell sx={getHeaderCellStyle(150)}>Вид кредитного продукта</TableCell>
                <TableCell sx={getHeaderCellStyle(120)}>Сумма лимита, руб.</TableCell>
                <TableCell sx={getHeaderCellStyle(120)}>Срок кредитования с</TableCell>
                <TableCell sx={getHeaderCellStyle(120)}>Срок кредитования по</TableCell>
                <TableCell sx={getHeaderCellStyle(160)}>% ставка годовых / комиссия по БГ</TableCell>
                <TableCell sx={getHeaderCellStyle(130)}>Финансовое состояние</TableCell>
                <TableCell sx={getHeaderCellStyle(150)}>Качество обслуживания долга</TableCell>
                <TableCell sx={getHeaderCellStyle(140)}>Категория качества ссуды</TableCell>
                <TableCell sx={getHeaderCellStyle(90)}>% резерва</TableCell>
                <TableCell sx={sdmTableStyles.emptyCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.existingObligations.map((row, rowIndex) => (
                <TableRow key={rowIndex} sx={sdmTableStyles.dataRow}>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.borrowerName}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "borrowerName", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.creditProductType}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "creditProductType", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <FormattedAmountInput
                      value={row.limitAmount}
                      onChange={(val) => handleTableCellChange("existingObligations", rowIndex, "limitAmount", val)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="date"
                      value={row.creditTermFrom}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "creditTermFrom", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="date"
                      value={row.creditTermTo}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "creditTermTo", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.rateOrCommission}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "rateOrCommission", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.financialStatus}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "financialStatus", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.debtServiceQuality}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "debtServiceQuality", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.loanQualityCategory}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "loanQualityCategory", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.reservePercentage}
                      onChange={(e) => handleTableCellChange("existingObligations", rowIndex, "reservePercentage", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRow("existingObligations", rowIndex)}
                      sx={sdmTableStyles.deleteButton}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={11} sx={sdmTableStyles.addRowCell}>
                  <Button
                    fullWidth
                    onClick={() => handleAddRow("existingObligations")}
                    startIcon={<AddCircleIcon />}
                    sx={sdmTableStyles.addRowButton}
                  >
                    Добавить строку
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Total limit for the Borrower */}
      <Box sx={sdmTableStyles.tableWrapper}>
        <Box sx={sdmTableStyles.headerSection}>
          <Box sx={sdmTableStyles.sectionTitle}>
            Общий лимит на Заемщика / Группу связанных заемщиков с учетом вновь запрашиваемого кредитного продукта / предлагаемых изменений
          </Box>
        </Box>
        <TableContainer component={Paper} elevation={0} sx={sdmTableStyles.tableContainer}>
          <Table size={sdmTableStyles.tableSize}>
            <TableHead>
              <TableRow sx={sdmTableStyles.headerRow}>
                <TableCell sx={getHeaderCellStyle(150)}>Наименование заемщика</TableCell>
                <TableCell sx={getHeaderCellStyle(150)}>Вид кредитного продукта</TableCell>
                <TableCell sx={getHeaderCellStyle(120)}>Сумма лимита, руб.</TableCell>
                <TableCell sx={getHeaderCellStyle(120)}>Срок кредитования с</TableCell>
                <TableCell sx={getHeaderCellStyle(120)}>Срок кредитования по</TableCell>
                <TableCell sx={getHeaderCellStyle(160)}>% ставка годовых / комиссия по БГ</TableCell>
                <TableCell sx={getHeaderCellStyle(130)}>Финансовое состояние</TableCell>
                <TableCell sx={getHeaderCellStyle(150)}>Качество обслуживания долга</TableCell>
                <TableCell sx={getHeaderCellStyle(140)}>Категория качества ссуды</TableCell>
                <TableCell sx={getHeaderCellStyle(90)}>% резерва</TableCell>
                <TableCell sx={sdmTableStyles.emptyCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.totalLimit.map((row, rowIndex) => (
                <TableRow key={rowIndex} sx={sdmTableStyles.dataRow}>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.borrowerName}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "borrowerName", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.creditProductType}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "creditProductType", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <FormattedAmountInput
                      value={row.limitAmount}
                      onChange={(val) => handleTableCellChange("totalLimit", rowIndex, "limitAmount", val)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="date"
                      value={row.creditTermFrom}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "creditTermFrom", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="date"
                      value={row.creditTermTo}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "creditTermTo", e.target.value)}
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.rateOrCommission}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "rateOrCommission", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.financialStatus}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "financialStatus", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.debtServiceQuality}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "debtServiceQuality", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.loanQualityCategory}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "loanQualityCategory", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <TextField
                      type="text"
                      value={row.reservePercentage}
                      onChange={(e) => handleTableCellChange("totalLimit", rowIndex, "reservePercentage", e.target.value)}
                      placeholder="Введите"
                      size="small"
                      variant="outlined"
                      fullWidth
                      sx={sdmTableStyles.textField}
                    />
                  </TableCell>
                  <TableCell sx={sdmTableStyles.dataCell}>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteRow("totalLimit", rowIndex)}
                      sx={sdmTableStyles.deleteButton}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={11} sx={sdmTableStyles.addRowCell}>
                  <Button
                    fullWidth
                    onClick={() => handleAddRow("totalLimit")}
                    startIcon={<AddCircleIcon />}
                    sx={sdmTableStyles.addRowButton}
                  >
                    Добавить строку
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default BorrowerInfoTab;
